import React, { useState, useEffect, useCallback } from 'react';
import { workspaceTeamAPI } from '../services/api';

const WorkspaceTeam = () => {
  const [members, setMembers] = useState([]);
  const [totalActive, setTotalActive] = useState(0);
  const [roles, setRoles] = useState({});
  const [allPermissions, setAllPermissions] = useState({});
  const [activityLog, setActivityLog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(null);
  const [showLogModal, setShowLogModal] = useState(false);
  const [inviteForm, setInviteForm] = useState({ email: '', role: 'vendeur', custom_role: '', permissions: [], notes: '' });
  const [editForm, setEditForm] = useState({ role: '', custom_role: '', permissions: [], notes: '' });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [inviteLoading, setInviteLoading] = useState(false);

  // Filtres journal d'activité
  const [logSearch, setLogSearch] = useState('');
  const [logFilterMember, setLogFilterMember] = useState('all');
  const [logFilterAction, setLogFilterAction] = useState('all');
  const [logFilterDate, setLogFilterDate] = useState('all');

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [membersRes, rolesRes] = await Promise.all([
        workspaceTeamAPI.getMembers(),
        workspaceTeamAPI.getRoles()
      ]);
      setMembers(membersRes.data.members || []);
      setTotalActive(membersRes.data.total_active || 0);
      setRoles(rolesRes.data.roles || {});
      setAllPermissions(rolesRes.data.permissions || {});
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleInvite = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setInviteLoading(true);

    try {
      const payload = {
        email: inviteForm.email,
        role: inviteForm.role,
        notes: inviteForm.notes || null
      };
      if (inviteForm.role === 'custom') {
        payload.custom_role = inviteForm.custom_role;
        payload.permissions = inviteForm.permissions;
      }

      const res = await workspaceTeamAPI.invite(payload);
      setSuccess(res.data.message || 'Invitation envoyée !');
      setShowInviteModal(false);
      setInviteForm({ email: '', role: 'vendeur', custom_role: '', permissions: [], notes: '' });
      loadData();
    } catch (err) {
      setError(err.response?.data?.error || 'Erreur lors de l\'envoi de l\'invitation');
    } finally {
      setInviteLoading(false);
    }
  };

  const handleUpdate = async (memberId) => {
    setError('');
    try {
      const payload = { ...editForm };
      if (editForm.role !== 'custom') {
        delete payload.custom_role;
      }
      await workspaceTeamAPI.updateMember(memberId, payload);
      setSuccess('Membre mis à jour !');
      setShowEditModal(null);
      loadData();
    } catch (err) {
      setError(err.response?.data?.error || 'Erreur lors de la mise à jour');
    }
  };

  const handleRemove = async (memberId, memberName) => {
    if (!window.confirm(`Êtes-vous sûr de vouloir retirer ${memberName} de votre équipe ?`)) return;
    try {
      await workspaceTeamAPI.removeMember(memberId);
      setSuccess(`${memberName} a été retiré(e) de l'équipe`);
      loadData();
    } catch (err) {
      setError(err.response?.data?.error || 'Erreur lors du retrait');
    }
  };

  const loadActivityLog = async () => {
    try {
      const res = await workspaceTeamAPI.getActivityLog(1000);
      setActivityLog(res.data.activities || []);
      setShowLogModal(true);
    } catch (err) {
      console.error(err);
    }
  };

  const openEditModal = (member) => {
    setEditForm({
      role: member.role,
      custom_role: member.custom_role || '',
      permissions: member.permissions || [],
      notes: member.notes || ''
    });
    setShowEditModal(member);
  };

  const handleRoleChange = (role, formSetter, form) => {
    const defaultPerms = roles[role]?.permissions || [];
    formSetter({ ...form, role, permissions: [...defaultPerms] });
  };

  const togglePermission = (perm, formSetter, form) => {
    const perms = form.permissions || [];
    if (perms.includes(perm)) {
      formSetter({ ...form, permissions: perms.filter(p => p !== perm) });
    } else {
      formSetter({ ...form, permissions: [...perms, perm] });
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-yellow-100 text-yellow-800',
      accepted: 'bg-green-100 text-green-800',
      declined: 'bg-red-100 text-red-800',
      revoked: 'bg-gray-100 text-gray-800'
    };
    const labels = {
      pending: '⏳ En attente',
      accepted: '✅ Actif',
      declined: '❌ Refusé',
      revoked: '🚫 Retiré'
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${styles[status] || 'bg-gray-100 text-gray-600'}`}>
        {labels[status] || status}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="p-6 flex justify-center">
        <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 max-w-6xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-gray-900">👥 Mon Équipe</h1>
            <p className="text-gray-500 mt-1">
              Gérez les membres qui ont accès à votre espace de travail
            </p>
          </div>
          <div className="flex gap-3">
            <button
              onClick={loadActivityLog}
              className="px-4 py-2 bg-gray-100 text-gray-700 rounded-xl hover:bg-gray-200 transition flex items-center gap-2"
            >
              <span>📋</span>
              <span className="hidden sm:inline">Activité</span>
            </button>
            <button
              onClick={() => setShowInviteModal(true)}
              className="px-4 py-2 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 transition flex items-center gap-2 shadow-lg"
            >
              <span>➕</span>
              <span>Inviter</span>
            </button>
          </div>
        </div>

        {/* Stats */}
        <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-3">
          <div className="bg-green-50 border border-green-200 rounded-xl p-3 text-center">
            <div className="text-2xl font-bold text-green-700">{totalActive}</div>
            <div className="text-xs text-green-600">Membres actifs</div>
          </div>
          <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-3 text-center">
            <div className="text-2xl font-bold text-yellow-700">
              {members.filter(m => m.invitation_status === 'pending').length}
            </div>
            <div className="text-xs text-yellow-600">En attente</div>
          </div>
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 text-center">
            <div className="text-2xl font-bold text-blue-700">{members.length}</div>
            <div className="text-xs text-blue-600">Total invités</div>
          </div>
          <div className="bg-purple-50 border border-purple-200 rounded-xl p-3 text-center">
            <div className="text-2xl font-bold text-purple-700">
              {new Set(members.filter(m => m.invitation_status === 'accepted').map(m => m.role)).size}
            </div>
            <div className="text-xs text-purple-600">Rôles actifs</div>
          </div>
        </div>
      </div>

      {/* Alertes */}
      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 text-red-700 rounded-xl flex items-center gap-2">
          <span>⚠️</span> {error}
          <button onClick={() => setError('')} className="ml-auto text-red-400 hover:text-red-600">✕</button>
        </div>
      )}
      {success && (
        <div className="mb-4 p-4 bg-green-50 border border-green-200 text-green-700 rounded-xl flex items-center gap-2">
          <span>✅</span> {success}
          <button onClick={() => setSuccess('')} className="ml-auto text-green-400 hover:text-green-600">✕</button>
        </div>
      )}

      {/* Liste des membres */}
      {members.length === 0 ? (
        <div className="bg-white rounded-2xl shadow-sm border p-8 text-center">
          <span className="text-6xl mb-4 block">👥</span>
          <h3 className="text-xl font-bold text-gray-800 mb-2">Aucun membre dans votre équipe</h3>
          <p className="text-gray-500 mb-6">
            Invitez des utilisateurs DJAMANN pour qu'ils puissent gérer votre workspace
            selon le rôle que vous leur attribuez.
          </p>
          <button
            onClick={() => setShowInviteModal(true)}
            className="px-6 py-3 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 transition font-medium"
          >
            ➕ Inviter mon premier membre
          </button>
        </div>
      ) : (
        <div className="grid gap-4">
          {members.map(member => (
            <div key={member.id} className="bg-white rounded-2xl shadow-sm border p-4 md:p-5 hover:shadow-md transition">
              <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                {/* Avatar & Info */}
                <div className="flex items-center gap-3 flex-1">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-indigo-400 to-purple-500 flex items-center justify-center text-white text-xl font-bold flex-shrink-0">
                    {member.member_photo ? (
                      <img src={member.member_photo} alt="" className="w-12 h-12 rounded-full object-cover" />
                    ) : (
                      member.member_name?.charAt(0)?.toUpperCase() || '?'
                    )}
                  </div>
                  <div className="min-w-0">
                    <div className="font-semibold text-gray-900 truncate">{member.member_name}</div>
                    <div className="text-sm text-gray-500 truncate">{member.member_email}</div>
                    {member.member_telephone && (
                      <div className="text-xs text-gray-400">{member.member_telephone}</div>
                    )}
                  </div>
                </div>

                {/* Rôle */}
                <div className="flex items-center gap-2">
                  <span className="text-xl">{member.role_info?.icon || '⚙️'}</span>
                  <div>
                    <div className="font-medium text-gray-800 text-sm">{member.role_label}</div>
                    <div className="text-xs text-gray-400">{member.permissions?.length || 0} permissions</div>
                  </div>
                </div>

                {/* Statut */}
                <div className="flex items-center gap-2">
                  {getStatusBadge(member.invitation_status)}
                </div>

                {/* Actions */}
                {member.invitation_status === 'accepted' && (
                  <div className="flex gap-2">
                    <button
                      onClick={() => openEditModal(member)}
                      className="px-3 py-1.5 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 transition text-sm"
                    >
                      ✏️ Modifier
                    </button>
                    <button
                      onClick={() => handleRemove(member.member_id, member.member_name)}
                      className="px-3 py-1.5 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition text-sm"
                    >
                      ❌ Retirer
                    </button>
                  </div>
                )}
                {member.invitation_status === 'pending' && (
                  <div className="text-xs text-gray-400">
                    Invité le {new Date(member.invited_at).toLocaleDateString('fr-FR')}
                  </div>
                )}
              </div>

              {/* Permissions summary */}
              {member.invitation_status === 'accepted' && member.permissions?.length > 0 && (
                <div className="mt-3 pt-3 border-t flex flex-wrap gap-1">
                  {member.permissions.slice(0, 6).map(perm => (
                    <span key={perm} className="px-2 py-0.5 bg-gray-100 text-gray-600 rounded text-xs">
                      {allPermissions[perm] || perm}
                    </span>
                  ))}
                  {member.permissions.length > 6 && (
                    <span className="px-2 py-0.5 bg-indigo-100 text-indigo-600 rounded text-xs">
                      +{member.permissions.length - 6} autres
                    </span>
                  )}
                </div>
              )}

              {/* Notes */}
              {member.notes && (
                <div className="mt-2 text-xs text-gray-400 italic">📝 {member.notes}</div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* MODAL: Inviter un membre */}
      {showInviteModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setShowInviteModal(false)}>
          <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="p-6 border-b">
              <h2 className="text-xl font-bold text-gray-900">➕ Inviter un membre</h2>
              <p className="text-sm text-gray-500 mt-1">
                L'utilisateur invité gardera son propre workspace intact
              </p>
            </div>

            <form onSubmit={handleInvite} className="p-6 space-y-4">
              {/* Email */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email DJAMANN *</label>
                <input
                  type="email"
                  value={inviteForm.email}
                  onChange={e => setInviteForm({ ...inviteForm, email: e.target.value })}
                  className="w-full px-4 py-2.5 border rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  placeholder="email@exemple.com"
                  required
                />
                <p className="text-xs text-gray-400 mt-1">L'utilisateur doit déjà avoir un compte DJAMANN</p>
              </div>

              {/* Rôle */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Rôle / Titre *</label>
                <div className="grid grid-cols-2 gap-2">
                  {Object.entries(roles).map(([key, role]) => (
                    <button
                      key={key}
                      type="button"
                      onClick={() => handleRoleChange(key, setInviteForm, inviteForm)}
                      className={`p-3 rounded-xl border-2 text-left transition ${
                        inviteForm.role === key
                          ? 'border-indigo-500 bg-indigo-50'
                          : 'border-gray-200 hover:border-indigo-300'
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        <span className="text-xl">{role.icon}</span>
                        <span className="text-sm font-medium">{role.label}</span>
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Rôle personnalisé */}
              {inviteForm.role === 'custom' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Nom du rôle personnalisé</label>
                  <input
                    type="text"
                    value={inviteForm.custom_role}
                    onChange={e => setInviteForm({ ...inviteForm, custom_role: e.target.value })}
                    className="w-full px-4 py-2.5 border rounded-xl focus:ring-2 focus:ring-indigo-500"
                    placeholder="Ex: Assistant commercial"
                  />
                </div>
              )}

              {/* Permissions */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Permissions ({inviteForm.permissions?.length || 0} sélectionnées)
                </label>
                <div className="bg-gray-50 rounded-xl p-3 max-h-48 overflow-y-auto space-y-1">
                  {Object.entries(allPermissions).map(([key, label]) => (
                    <label key={key} className="flex items-center gap-2 py-1 px-2 rounded hover:bg-white cursor-pointer">
                      <input
                        type="checkbox"
                        checked={inviteForm.permissions?.includes(key)}
                        onChange={() => togglePermission(key, setInviteForm, inviteForm)}
                        className="rounded text-indigo-600 focus:ring-indigo-500"
                      />
                      <span className="text-sm text-gray-700">{label}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Notes */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Notes (optionnel)</label>
                <textarea
                  value={inviteForm.notes}
                  onChange={e => setInviteForm({ ...inviteForm, notes: e.target.value })}
                  className="w-full px-4 py-2.5 border rounded-xl focus:ring-2 focus:ring-indigo-500 resize-none"
                  rows={2}
                  placeholder="Notes internes sur ce membre..."
                />
              </div>

              {/* Actions */}
              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setShowInviteModal(false)}
                  className="flex-1 px-4 py-2.5 bg-gray-100 text-gray-700 rounded-xl hover:bg-gray-200 transition"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  disabled={inviteLoading}
                  className="flex-1 px-4 py-2.5 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 transition disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {inviteLoading ? (
                    <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent"></div>
                  ) : (
                    <>📨 Envoyer l'invitation</>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* MODAL: Modifier un membre */}
      {showEditModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setShowEditModal(null)}>
          <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="p-6 border-b">
              <h2 className="text-xl font-bold text-gray-900">✏️ Modifier {showEditModal.member_name}</h2>
            </div>

            <div className="p-6 space-y-4">
              {/* Rôle */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Rôle</label>
                <div className="grid grid-cols-2 gap-2">
                  {Object.entries(roles).map(([key, role]) => (
                    <button
                      key={key}
                      type="button"
                      onClick={() => handleRoleChange(key, setEditForm, editForm)}
                      className={`p-3 rounded-xl border-2 text-left transition ${
                        editForm.role === key
                          ? 'border-indigo-500 bg-indigo-50'
                          : 'border-gray-200 hover:border-indigo-300'
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        <span className="text-xl">{role.icon}</span>
                        <span className="text-sm font-medium">{role.label}</span>
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              {editForm.role === 'custom' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Nom du rôle personnalisé</label>
                  <input
                    type="text"
                    value={editForm.custom_role}
                    onChange={e => setEditForm({ ...editForm, custom_role: e.target.value })}
                    className="w-full px-4 py-2.5 border rounded-xl focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
              )}

              {/* Permissions */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Permissions ({editForm.permissions?.length || 0})
                </label>
                <div className="bg-gray-50 rounded-xl p-3 max-h-48 overflow-y-auto space-y-1">
                  {Object.entries(allPermissions).map(([key, label]) => (
                    <label key={key} className="flex items-center gap-2 py-1 px-2 rounded hover:bg-white cursor-pointer">
                      <input
                        type="checkbox"
                        checked={editForm.permissions?.includes(key)}
                        onChange={() => togglePermission(key, setEditForm, editForm)}
                        className="rounded text-indigo-600 focus:ring-indigo-500"
                      />
                      <span className="text-sm text-gray-700">{label}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Notes */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                <textarea
                  value={editForm.notes}
                  onChange={e => setEditForm({ ...editForm, notes: e.target.value })}
                  className="w-full px-4 py-2.5 border rounded-xl focus:ring-2 focus:ring-indigo-500 resize-none"
                  rows={2}
                />
              </div>

              {/* Actions */}
              <div className="flex gap-3 pt-2">
                <button
                  onClick={() => setShowEditModal(null)}
                  className="flex-1 px-4 py-2.5 bg-gray-100 text-gray-700 rounded-xl hover:bg-gray-200 transition"
                >
                  Annuler
                </button>
                <button
                  onClick={() => handleUpdate(showEditModal.member_id)}
                  className="flex-1 px-4 py-2.5 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 transition"
                >
                  💾 Enregistrer
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* MODAL: Journal d'activité — version avancée */}
      {showLogModal && (() => {
        // Traduction lisible des actions
        const ACTION_LABELS = {
          'view_dashboard': '👁️ A consulté le tableau de bord',
          'view_products': '👁️ A consulté les produits',
          'create_product': '➕ A créé un produit',
          'update_product': '✏️ A modifié un produit',
          'delete_product': '🗑️ A supprimé un produit',
          'view_clients': '👁️ A consulté les clients',
          'create_client': '➕ A ajouté un client',
          'update_client': '✏️ A modifié un client',
          'view_invoices': '👁️ A consulté les factures',
          'create_invoice': '➕ A créé une facture',
          'update_invoice': '✏️ A modifié une facture',
          'delete_invoice': '🗑️ A supprimé une facture',
          'view_quotes': '👁️ A consulté les devis',
          'create_quote': '➕ A créé un devis',
          'update_quote': '✏️ A modifié un devis',
          'delete_quote': '🗑️ A supprimé un devis',
          'view_payments': '👁️ A consulté les paiements',
          'create_payment': '💰 A enregistré un paiement',
          'update_payment': '✏️ A modifié un paiement',
          'view_expenses': '👁️ A consulté les dépenses',
          'create_expense': '💸 A enregistré une dépense',
          'update_expense': '✏️ A modifié une dépense',
          'view_debts': '👁️ A consulté les créances',
          'create_debt': '📄 A créé une créance',
          'update_debt': '✏️ A modifié une créance',
          'view_prestations': '👁️ A consulté les prestations',
          'create_prestation': '➕ A ajouté une prestation',
          'view_restock': '👁️ A consulté le réapprovisionnement',
          'create_restock': '📦 A fait un réapprovisionnement',
          'view_report': '📊 A consulté les rapports',
          'view_settings': '⚙️ A consulté les paramètres',
          'update_settings': '⚙️ A modifié les paramètres',
          'view_shop': '🏪 A consulté la boutique',
          'update_shop': '🏪 A modifié la boutique',
          'accept_invitation': '✅ A accepté l\'invitation',
          'decline_invitation': '❌ A refusé l\'invitation',
          'remove_member': '🚫 Membre retiré de l\'équipe',
          'left_team': '👋 A quitté l\'équipe',
          'update_member': '✏️ Rôle/permissions modifiés',
          'invite_member': '📨 Invitation envoyée',
        };

        const ACTION_CATEGORIES = {
          'all': { label: 'Toutes les actions', icon: '📋' },
          'view': { label: 'Consultations', icon: '👁️' },
          'create': { label: 'Créations', icon: '➕' },
          'update': { label: 'Modifications', icon: '✏️' },
          'delete': { label: 'Suppressions', icon: '🗑️' },
          'team': { label: 'Équipe', icon: '👥' },
        };

        const getActionCategory = (action) => {
          if (['accept_invitation', 'decline_invitation', 'remove_member', 'left_team', 'update_member', 'invite_member'].includes(action)) return 'team';
          if (action.startsWith('view')) return 'view';
          if (action.startsWith('create')) return 'create';
          if (action.startsWith('update')) return 'update';
          if (action.startsWith('delete')) return 'delete';
          return 'other';
        };

        const getActionColor = (action) => {
          const cat = getActionCategory(action);
          const colors = {
            view: 'bg-blue-100 text-blue-700 border-blue-200',
            create: 'bg-green-100 text-green-700 border-green-200',
            update: 'bg-amber-100 text-amber-700 border-amber-200',
            delete: 'bg-red-100 text-red-700 border-red-200',
            team: 'bg-purple-100 text-purple-700 border-purple-200',
          };
          return colors[cat] || 'bg-gray-100 text-gray-700 border-gray-200';
        };

        const isInDateRange = (dateStr) => {
          if (logFilterDate === 'all') return true;
          const d = new Date(dateStr);
          const now = new Date();
          if (logFilterDate === 'today') {
            return d.toDateString() === now.toDateString();
          }
          if (logFilterDate === '7days') {
            return (now - d) < 7 * 24 * 60 * 60 * 1000;
          }
          if (logFilterDate === '30days') {
            return (now - d) < 30 * 24 * 60 * 60 * 1000;
          }
          return true;
        };

        // Membres uniques dans le log
        const uniqueMembers = [...new Map(activityLog.map(l => [l.member_id, { id: l.member_id, name: l.member_name }])).values()];

        // Filtrage
        const filtered = activityLog.filter(log => {
          if (logFilterMember !== 'all' && String(log.member_id) !== logFilterMember) return false;
          if (logFilterAction !== 'all' && getActionCategory(log.action) !== logFilterAction) return false;
          if (!isInDateRange(log.created_at)) return false;
          if (logSearch) {
            const q = logSearch.toLowerCase();
            const label = (ACTION_LABELS[log.action] || log.action).toLowerCase();
            const name = (log.member_name || '').toLowerCase();
            const details = (log.details || '').toLowerCase();
            if (!label.includes(q) && !name.includes(q) && !details.includes(q)) return false;
          }
          return true;
        });

        // Regrouper par date
        const grouped = {};
        filtered.forEach(log => {
          const dateKey = new Date(log.created_at).toLocaleDateString('fr-FR', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
          if (!grouped[dateKey]) grouped[dateKey] = [];
          grouped[dateKey].push(log);
        });

        // Stats rapides
        const stats = {
          total: activityLog.length,
          views: activityLog.filter(l => l.action.startsWith('view')).length,
          creates: activityLog.filter(l => l.action.startsWith('create')).length,
          updates: activityLog.filter(l => l.action.startsWith('update')).length,
          deletes: activityLog.filter(l => l.action.startsWith('delete')).length,
        };

        return (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-2 sm:p-4" onClick={() => setShowLogModal(false)}>
          <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[95vh] flex flex-col" onClick={e => e.stopPropagation()}>

            {/* Header */}
            <div className="p-4 sm:p-6 border-b flex-shrink-0">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h2 className="text-xl font-bold text-gray-900">📋 Journal d'activité</h2>
                  <p className="text-sm text-gray-500 mt-0.5">Suivez toutes les actions de votre équipe</p>
                </div>
                <button onClick={() => setShowLogModal(false)} className="w-8 h-8 flex items-center justify-center rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-500 hover:text-gray-700 transition">✕</button>
              </div>

              {/* Mini stats */}
              <div className="grid grid-cols-5 gap-2 mb-4">
                <div className="bg-gray-50 rounded-lg p-2 text-center">
                  <div className="text-lg font-bold text-gray-800">{stats.total}</div>
                  <div className="text-[10px] text-gray-500">Total</div>
                </div>
                <div className="bg-blue-50 rounded-lg p-2 text-center">
                  <div className="text-lg font-bold text-blue-700">{stats.views}</div>
                  <div className="text-[10px] text-blue-500">Vues</div>
                </div>
                <div className="bg-green-50 rounded-lg p-2 text-center">
                  <div className="text-lg font-bold text-green-700">{stats.creates}</div>
                  <div className="text-[10px] text-green-500">Créations</div>
                </div>
                <div className="bg-amber-50 rounded-lg p-2 text-center">
                  <div className="text-lg font-bold text-amber-700">{stats.updates}</div>
                  <div className="text-[10px] text-amber-500">Modifs</div>
                </div>
                <div className="bg-red-50 rounded-lg p-2 text-center">
                  <div className="text-lg font-bold text-red-700">{stats.deletes}</div>
                  <div className="text-[10px] text-red-500">Suppr.</div>
                </div>
              </div>

              {/* Barre de recherche */}
              <div className="relative mb-3">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">🔍</span>
                <input
                  type="text"
                  value={logSearch}
                  onChange={e => setLogSearch(e.target.value)}
                  placeholder="Rechercher une action, un membre, un détail..."
                  className="w-full pl-10 pr-4 py-2.5 border rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm"
                />
                {logSearch && (
                  <button onClick={() => setLogSearch('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">✕</button>
                )}
              </div>

              {/* Filtres */}
              <div className="flex flex-wrap gap-2">
                {/* Filtre membre */}
                <select
                  value={logFilterMember}
                  onChange={e => setLogFilterMember(e.target.value)}
                  className="px-3 py-1.5 border rounded-lg text-sm bg-white focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                >
                  <option value="all">👤 Tous les membres</option>
                  {uniqueMembers.map(m => (
                    <option key={m.id} value={String(m.id)}>{m.name}</option>
                  ))}
                </select>

                {/* Filtre type d'action */}
                <select
                  value={logFilterAction}
                  onChange={e => setLogFilterAction(e.target.value)}
                  className="px-3 py-1.5 border rounded-lg text-sm bg-white focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                >
                  {Object.entries(ACTION_CATEGORIES).map(([key, cat]) => (
                    <option key={key} value={key}>{cat.icon} {cat.label}</option>
                  ))}
                </select>

                {/* Filtre période */}
                <select
                  value={logFilterDate}
                  onChange={e => setLogFilterDate(e.target.value)}
                  className="px-3 py-1.5 border rounded-lg text-sm bg-white focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                >
                  <option value="all">📅 Toute la période</option>
                  <option value="today">Aujourd'hui</option>
                  <option value="7days">7 derniers jours</option>
                  <option value="30days">30 derniers jours</option>
                </select>

                {/* Reset filtres */}
                {(logSearch || logFilterMember !== 'all' || logFilterAction !== 'all' || logFilterDate !== 'all') && (
                  <button
                    onClick={() => { setLogSearch(''); setLogFilterMember('all'); setLogFilterAction('all'); setLogFilterDate('all'); }}
                    className="px-3 py-1.5 bg-red-50 text-red-600 rounded-lg text-sm hover:bg-red-100 transition"
                  >
                    ✕ Réinitialiser
                  </button>
                )}
              </div>
            </div>

            {/* Contenu scrollable */}
            <div className="flex-1 overflow-y-auto p-4 sm:p-6">
              {filtered.length === 0 ? (
                <div className="text-center py-12">
                  <span className="text-5xl block mb-3">🔍</span>
                  <p className="text-gray-500 font-medium">Aucune activité trouvée</p>
                  <p className="text-gray-400 text-sm mt-1">Essayez de modifier vos filtres</p>
                </div>
              ) : (
                <div className="space-y-6">
                  {Object.entries(grouped).map(([dateLabel, logs]) => (
                    <div key={dateLabel}>
                      {/* Séparateur de date */}
                      <div className="flex items-center gap-3 mb-3">
                        <div className="h-px bg-gray-200 flex-1"></div>
                        <span className="text-xs font-semibold text-gray-500 uppercase bg-gray-50 px-3 py-1 rounded-full whitespace-nowrap">
                          {dateLabel}
                        </span>
                        <div className="h-px bg-gray-200 flex-1"></div>
                      </div>

                      <div className="space-y-2">
                        {logs.map(log => (
                          <div key={log.id} className={`flex items-start gap-3 p-3 rounded-xl border transition hover:shadow-sm ${getActionColor(log.action)}`}>
                            {/* Avatar membre */}
                            <div className="w-9 h-9 rounded-full bg-white/80 border flex items-center justify-center flex-shrink-0 text-sm font-bold shadow-sm">
                              {log.member_name?.charAt(0)?.toUpperCase() || '?'}
                            </div>

                            <div className="flex-1 min-w-0">
                              {/* Nom + heure */}
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="font-semibold text-sm">{log.member_name}</span>
                                <span className="text-[11px] opacity-60">
                                  {new Date(log.created_at).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                                </span>
                              </div>

                              {/* Action lisible */}
                              <div className="text-sm mt-0.5">
                                {ACTION_LABELS[log.action] || `📝 ${log.action.replace(/_/g, ' ')}`}
                              </div>

                              {/* Détails */}
                              {log.details && (
                                <div className="text-xs mt-1 opacity-70 bg-white/50 rounded px-2 py-1 inline-block">
                                  💬 {log.details}
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Footer avec compteur */}
            <div className="p-3 border-t flex-shrink-0 flex items-center justify-between text-xs text-gray-500 bg-gray-50 rounded-b-2xl">
              <span>{filtered.length} action{filtered.length > 1 ? 's' : ''} affichée{filtered.length > 1 ? 's' : ''} sur {activityLog.length}</span>
              <button
                onClick={loadActivityLog}
                className="text-indigo-600 hover:text-indigo-700 font-medium flex items-center gap-1"
              >
                🔄 Actualiser
              </button>
            </div>
          </div>
        </div>
        );
      })()}
    </div>
  );
};

export default WorkspaceTeam;
